import { FC, ReactElement, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useModal } from '@ebay/nice-modal-react'
import clsx from 'clsx'
import { ChevronDown, Edit3, Plus, FolderPlus } from 'react-feather'

import {
  Dropdown,
  Menu,
  MenuItem,
  Divider,
  usePaginatedAssignmentList,
  Tooltip,
  useUtils,
  useProjectDetails,
  useGlobalState,
} from '@pepper/ui'
import { PROJECT_STATUS } from '@pepper/utils/src/constant/enum/project_status'

import ProjectActionsModal from 'components/Modals/ProjectActionsModal/ProjectActionsModal'

import styles from './_.module.css'
import { Icon } from '@iconify/react'

interface ProjectDropdownProps {
  icon: 'more' | 'chevron'
  projectName: string
  projectId: string
  className?: string
  projectStatus: string
  refetch?: () => void
}

const ProjectDropdown: FC<ProjectDropdownProps> = ({
  icon,
  projectName = '',
  projectId = '',
  className = '',
  projectStatus,
  refetch,
}) => {
  const confirmProjectDuplicationModal = useModal('confirm-project-duplication')
  const [modalStatus, setmodalStatus] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const serviceSelectionDrawer = useModal('service-selection-drawer')
  const location = useLocation()
  const { setPendingSidebarCountKeys } = useGlobalState()
  const { verticals } = useUtils()

  const { records: assignments, isLoading: isAssignmentsLoading } =
    usePaginatedAssignmentList(
      {
        project_id: projectId,
        favourite_creator: true,
        include_extra: true,
      },
      undefined,
      false,
      false,
      dropdownOpen
    )
  const { data: projectDetails } = useProjectDetails(
    projectId,
    {},
    { enabled: dropdownOpen }
  )

  const onRename = () => {
    setPendingSidebarCountKeys(['allProjectsCount'])
    setmodalStatus(true)
  }
  const onAddNewItem = () => {
    serviceSelectionDrawer.show({
      projectId,
      mode: 'ADD ASSIGNMENT',
      title: 'Pick a service',
      projectBody: {
        project_title: projectDetails?.project_title,
        document_brief_id: assignments[0]?.document_brief?.id,
        genre_id: assignments[0]?.genre.id,
        language_dialect: assignments[0]?.extra?.language_dialect,
        sub_genre_id: assignments[0]?.sub_genre?.id,
      },
    })
  }

  const dropdownOverlay = (): ReactElement => {
    const isAddAssignmentDisabled =
      isAssignmentsLoading ||
      [PROJECT_STATUS.scrapped.id, PROJECT_STATUS.hold.id].includes(
        projectStatus
      ) ||
      (projectDetails &&
        projectDetails.company?.type === 'enterprise' &&
        projectDetails.order_details !== null)

    const selectedVerticals = assignments.map(
      _assignment => _assignment.vertical?.id
    )

    const isDuplicateProjectDisabled =
      isAssignmentsLoading ||
      selectedVerticals.some(selectedVertical => {
        const isMatched = verticals.some(
          vertical => vertical.id === selectedVertical
        )

        return !isMatched
      })

    return (
      <Menu className={styles.dropdownMenu}>
        <>
          <MenuItem
            onClick={onRename}
            className={styles.dropdownMenuItem}
            data-id="business-project-dropdown-rename-project-menu-item"
          >
            <Edit3 size={16} className={styles.dropdownIcon} />
            <span>Rename</span>
          </MenuItem>

          <MenuItem
            disabled={isAddAssignmentDisabled}
            onClick={onAddNewItem}
            className={styles.dropdownMenuItem}
            data-id="addAssignment_projectDropdown"
          >
            <Tooltip
              destroyTooltipOnHide={true}
              arrowPointAtCenter={true}
              title={
                isAddAssignmentDisabled
                  ? 'Not available for this project'
                  : null
              }
            >
              <Plus size={16} className={styles.dropdownIcon} />
              <span>Add new Item</span>
            </Tooltip>
          </MenuItem>
        </>

        {assignments?.length > 0 ? (
          <>
            <Divider className={styles.divider} />
            <MenuItem
              disabled={isDuplicateProjectDisabled}
              onClick={() => confirmProjectDuplicationModal.show({ projectId })}
              className={styles.dropdownMenuItem}
              data-id="business-project-dropdown-duplicate-project-menu-item"
            >
              <Tooltip
                destroyTooltipOnHide={true}
                arrowPointAtCenter={true}
                title={
                  isDuplicateProjectDisabled
                    ? 'Not supported for this project'
                    : null
                }
              >
                <FolderPlus size={16} className={styles.dropdownIcon} />
                <span>Duplicate project</span>
              </Tooltip>
            </MenuItem>
          </>
        ) : null}
      </Menu>
    )
  }

  return (
    <div
      className={clsx(className, {
        [styles.iconContainer]: true,
        [styles.iconContainerMore]:
          icon === 'more' && location.pathname.includes(projectId),
        [styles.iconContainerChevron]: icon === 'chevron',
      })}
    >
      <Dropdown
        trigger={['click']}
        overlay={dropdownOverlay}
        placement="bottomRight"
        onVisibleChange={setDropdownOpen}
        className={styles.dropdown}
      >
        {icon === 'more' ? (
          <Icon icon="fe:elipsis-v" width={16} height={16} />
        ) : (
          <ChevronDown
            size={16}
            className={styles.dropdownIcon}
            data-id="business-project-dropdown-chevron-button"
          />
        )}
      </Dropdown>

      {modalStatus && (
        <ProjectActionsModal
          isOpen={modalStatus}
          onCancel={() => setmodalStatus(false)}
          projectName={projectName}
          projectId={projectId}
          onRename={refetch}
        ></ProjectActionsModal>
      )}
    </div>
  )
}

export default ProjectDropdown
